import { useEffect, useState } from 'react';
import type { UUID } from '../../helpers/validate/django-uuid';

/**
 * Preload the Pylon widget script early for better performance on slow connections.
 * This should be called as early as possible (e.g., in root.tsx) before user data is available.
 * It only loads the script stub and widget, without configuring user settings.
 */
const preloadPylonScript = (appId: string) => {
  if (!!appId && !window.Pylon) {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.text = `
      (function(){
        var e=window;
        var t=document;
        var n=function(){n.e(arguments)};
        n.q=[];
        n.e=function(e){n.q.push(e)};
        e.Pylon=n;
        var r=function(){
          var e=t.createElement("script");
          e.setAttribute("type","text/javascript");
          e.setAttribute("async","true");
          e.setAttribute("src","https://widget.usepylon.com/widget/${appId}");
          var n=t.getElementsByTagName("script")[0];
          n.parentNode.insertBefore(e,n)
        };
        if(t.readyState==="complete"){r()}
        else if(e.addEventListener){e.addEventListener("load",r,false)}
      })();
    `;
    document.head.appendChild(script);
  }
};

export const usePylonPreloadScript = (appId: string) => {
  const [preloaded, setPreloaded] = useState(false);
  useEffect(() => {
    preloadPylonScript(appId);
    if (!preloaded && window.Pylon) {
      setPreloaded(true);
    }
  }, [appId, preloaded]);
  return preloaded;
};

type PylonEnvVars = {
  appId: string;
};

type PylonUserContext = {
  id: UUID;
  email: string;
  firstName: string;
  lastName: string;
  hashedEmail: string;
  salesforceAccountId?: string | null | undefined;
  hubspotCompanyId?: string | null | undefined;
};

/**
 * Initialize Pylon chat with user context.
 * If the script hasn't been preloaded, this will load it first.
 */
export const initPylon = (
  envVars: PylonEnvVars,
  userContext: PylonUserContext,
  accountId?: string
) => {
  preloadPylonScript(envVars.appId);

  window.pylon = {
    chat_settings: {
      app_id: envVars.appId,
      email: userContext.email,
      name: `${userContext.firstName} ${userContext.lastName}`,
      email_hash: userContext.hashedEmail,
      account_id: accountId ?? undefined,
      account_external_id:
        userContext.hubspotCompanyId ??
        userContext.salesforceAccountId ??
        undefined,
    },
  };
};
